/**
 * useAuthenticatorChallenge — the login page's half of the push-2FA handshake.
 *
 * Implements the polling contract `GUIDES/PalmPuffin/authenticator/CHALLENGE_FLOW.md` specifies:
 * every **2 seconds**, at most **30 attempts** (60 s / 2 s), stopping on `approved` / `denied` /
 * `expired`. `SECURITY_MODEL.md` adds the offline fallback: surface **"Use code instead" after 10
 * seconds** without a response.
 *
 * ## 🔴 Why the timing constants are exported
 *
 * They are a CONTRACT with the server, not a UI preference: the challenge TTL is 60 s, and a poll
 * interval that drifts past it turns "expired" into "the login page hung". Exporting them is what
 * lets a test assert the contract instead of re-deriving it.
 *
 * @module @puffin-core/login-shell/hooks/useAuthenticatorChallenge
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/** CHALLENGE_FLOW.md "Poll interval — 2 seconds". @constant {number} */
export const CHALLENGE_POLL_INTERVAL_MS = 2000;

/** CHALLENGE_FLOW.md "Max poll attempts — 30" (60 s / 2 s). @constant {number} */
export const CHALLENGE_MAX_POLL_ATTEMPTS = 30;

/** SECURITY_MODEL.md "Login app shows 'Use code instead' after 10 seconds". @constant {number} */
export const CHALLENGE_FALLBACK_DELAY_MS = 10000;

/** The four states the server can report. @constant {Object} */
export const CHALLENGE_STATE = Object.freeze({
  PENDING: 'pending',
  APPROVED: 'approved',
  DENIED: 'denied',
  EXPIRED: 'expired',
});

/** Terminal states — polling stops. @constant {Set<string>} */
const TERMINAL = new Set([
  CHALLENGE_STATE.APPROVED,
  CHALLENGE_STATE.DENIED,
  CHALLENGE_STATE.EXPIRED,
]);

/**
 * @param {Object} options
 * @param {string|null} options.challengeId - Null disables polling entirely.
 * @param {Function} options.pollStatus - `async (challengeId) => { status, ...tokens }`. The app's
 *   own API client; this hook never constructs a URL, because the path differs per app mount.
 * @param {Function} [options.onApproved] - `(payload) => void`, called ONCE with the approved
 *   poll's full payload (which carries the tokens the server minted on consumption).
 * @param {Function} [options.onResolved] - `(status) => void`, called once for `denied`/`expired`.
 * @param {number} [options.pollIntervalMs]
 * @param {number} [options.maxAttempts]
 * @param {number} [options.fallbackDelayMs]
 * @returns {{status: string, attempts: number, showFallback: boolean, error: string|null,
 *   isPolling: boolean}}
 */
export function useAuthenticatorChallenge({
  challengeId,
  pollStatus,
  onApproved,
  onResolved,
  pollIntervalMs = CHALLENGE_POLL_INTERVAL_MS,
  maxAttempts = CHALLENGE_MAX_POLL_ATTEMPTS,
  fallbackDelayMs = CHALLENGE_FALLBACK_DELAY_MS,
}) {
  const [status, setStatus] = useState(CHALLENGE_STATE.PENDING);
  const [attempts, setAttempts] = useState(0);
  // ⚠️ `fallbackElapsed` is the raw timer, and `showFallback` below is DERIVED from it plus the
  // status. Storing the derived value directly made the flag depend on which of two timers jest
  // (or a browser under load) happened to run first: the 10-second fallback timer can fire in the
  // same tick as a 2-second poll whose promise has not yet resolved, and the user would briefly
  // see "Use code instead" on a challenge that was already approved.
  const [fallbackElapsed, setFallbackElapsed] = useState(false);
  const [error, setError] = useState(null);

  // 🔴 Callback refs, not dependencies. A parent that passes an inline arrow would otherwise tear
  // the interval down and rebuild it on every render — which silently resets the attempt budget
  // and makes the 60-second timeout never fire.
  const onApprovedRef = useRef(onApproved);
  const onResolvedRef = useRef(onResolved);
  const pollStatusRef = useRef(pollStatus);
  onApprovedRef.current = onApproved;
  onResolvedRef.current = onResolved;
  pollStatusRef.current = pollStatus;

  /** Guards against a late in-flight poll resolving after a terminal verdict. @type {Object} */
  const settledRef = useRef(false);

  useEffect(() => {
    if (!challengeId) return undefined;

    settledRef.current = false;
    setStatus(CHALLENGE_STATE.PENDING);
    setAttempts(0);
    setFallbackElapsed(false);
    setError(null);

    let attempt = 0;
    let cancelled = false;

    /**
     * Awaits `onApproved` — installing the approved session is asynchronous, and a session that
     * could not be saved REJECTS. The approved poll awaits this inside its try, so that rejection
     * lands in the same catch as a failed poll and is shown as the challenge's error.
     *
     * @param {string} nextStatus
     * @param {Object} [payload]
     * @returns {Promise<void>}
     */
    const settle = async (nextStatus, payload) => {
      if (settledRef.current) return;
      settledRef.current = true;
      setStatus(nextStatus);
      if (nextStatus === CHALLENGE_STATE.APPROVED) {
        if (onApprovedRef.current) await onApprovedRef.current(payload);
      } else if (onResolvedRef.current) {
        onResolvedRef.current(nextStatus);
      }
    };

    const interval = setInterval(async () => {
      if (cancelled || settledRef.current) return;

      attempt += 1;
      setAttempts(attempt);

      // The budget is spent: the challenge's own 60-second TTL has passed, so the server would
      // answer `expired` anyway. Settling locally saves one round trip on a dead challenge.
      if (attempt > maxAttempts) {
        await settle(CHALLENGE_STATE.EXPIRED);
        return;
      }

      try {
        const result = await pollStatusRef.current(challengeId);
        if (cancelled) return;
        const next = result && result.status;
        if (TERMINAL.has(next)) {
          await settle(next, result);
        }
      } catch (pollError) {
        if (cancelled) return;
        // 🔴 A transient network blip must NOT end the challenge — the user's phone may be about
        // to approve. The error is surfaced and polling continues until the attempt budget runs
        // out, which is the honest end of the window.
        setError(pollError && pollError.message ? pollError.message : String(pollError));
      }
    }, pollIntervalMs);

    // No `cancelled` guard here, deliberately: the cleanup `clearTimeout`s this timer, so unlike
    // the interval — which has an async gap after `await pollStatus(…)` where an unmount can land
    // mid-flight — this callback cannot run after teardown. A guard on an unreachable path is dead
    // code that reads like a real protection.
    const fallbackTimer = setTimeout(() => setFallbackElapsed(true), fallbackDelayMs);

    return () => {
      cancelled = true;
      clearInterval(interval);
      clearTimeout(fallbackTimer);
    };
  }, [challengeId, pollIntervalMs, maxAttempts, fallbackDelayMs]);

  const clearError = useCallback(() => setError(null), []);

  return {
    status,
    attempts,
    // Derived, never stored: a terminal challenge never offers the fallback, whatever the timer did.
    showFallback: fallbackElapsed && !TERMINAL.has(status),
    error,
    clearError,
    isPolling: Boolean(challengeId) && status === CHALLENGE_STATE.PENDING,
  };
}
